import { useState, useEffect, useCallback, useMemo, useRef } from "react";
import ColasService from "../api/colas.api";
import { clasificarEntradas } from "../helpers/turnos";

const MENSAJE_SIN_COLA = "Falta la cola";
const MENSAJE_ERROR_CARGA = "No se pudo cargar la cola";

/**
 * Hook de la pantalla pública de turnos: carga la cola y expone las listas ya
 * clasificadas. `loading` solo es true en la carga inicial (o al cambiar de id);
 * las recargas posteriores son silenciosas, se coalescen (como mucho una petición
 * extra tras la que está en vuelo) y un fallo conserva los datos pintados.
 * @param {string|null} id - Id de la cola (?id= de la URL)
 * @returns {{
 *   cola: Object|null,
 *   actual: Object|null,
 *   llamados: Array,
 *   esperando: Array,
 *   loading: boolean,
 *   error: string|null,
 *   recargar: () => Promise<void>
 * }} Estado de la pantalla
 */
export const usePantallaTurnos = (id) => {
    const [cola, setCola] = useState(null);
    const [loading, setLoading] = useState(Boolean(id));
    const [errorCarga, setErrorCarga] = useState(null);
    // Id al que pertenece el estado de arriba: mientras el efecto no lo sincroniza tras
    // un cambio de id, el render deriva loading/cola/error en vez de mostrar los del id anterior
    const [idSincronizado, setIdSincronizado] = useState(id);
    const enVuelo = useRef(false);
    const pendiente = useRef(false);
    const tieneDatos = useRef(false);
    // Cada cambio de id y el desmontaje incrementan la versión: lo que responda
    // una versión anterior se descarta
    const version = useRef(0);

    const recargar = useCallback(async () => {
        if (!id) return;
        if (enVuelo.current) {
            pendiente.current = true;
            return;
        }
        const miVersion = version.current;
        const vigente = () => miVersion === version.current;
        const silenciosa = tieneDatos.current;
        enVuelo.current = true;
        if (!silenciosa) {
            setLoading(true);
            setErrorCarga(null);
        }
        try {
            const data = await ColasService.getById(id);
            if (!vigente()) return;
            tieneDatos.current = true;
            setCola(data);
            setErrorCarga(null);
        } catch (err) {
            console.error("Error al cargar los turnos:", err);
            if (vigente() && !silenciosa) setErrorCarga(err.message || MENSAJE_ERROR_CARGA);
        } finally {
            if (vigente()) {
                enVuelo.current = false;
                setLoading(false);
                if (pendiente.current) {
                    pendiente.current = false;
                    recargar();
                }
            }
        }
    }, [id]);

    useEffect(() => {
        version.current += 1;
        enVuelo.current = false;
        pendiente.current = false;
        tieneDatos.current = false;
        setCola(null);
        setErrorCarga(null);
        setLoading(Boolean(id));
        setIdSincronizado(id);
        recargar();
        return () => {
            version.current += 1;
        };
    }, [id, recargar]);

    const sincronizado = idSincronizado === id;
    const colaVisible = sincronizado ? cola : null;
    const entradas = colaVisible?.entradas;
    const { actual, llamados, esperando } = useMemo(() => clasificarEntradas(entradas), [entradas]);

    return {
        cola: colaVisible, actual, llamados, esperando,
        loading: sincronizado ? loading : Boolean(id),
        error: id ? (sincronizado ? errorCarga : null) : MENSAJE_SIN_COLA,
        recargar,
    };
};
